const CPF_LENGTH = 11;
const CNPJ_LENGTH = 14;

class MacalCartTaxFields extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#handleChange);
    this.addEventListener('input', this.#handleInput);
    this.#syncFields();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.removeEventListener('input', this.#handleInput);
  }

  #handleChange = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.name === 'attributes[tipo_pessoa]') {
      this.#syncFields();
      this.#clearError();
    }
  };

  #handleInput = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    if (event.target.dataset.taxInput === 'cpf') {
      event.target.value = formatCpf(event.target.value);
    }

    if (event.target.dataset.taxInput === 'cnpj') {
      event.target.value = formatCnpj(event.target.value);
    }

    event.target.removeAttribute('aria-invalid');
    this.#clearError();
  };

  #syncFields() {
    const type = this.taxType;
    const cpfField = this.querySelector('[data-tax-field="cpf"]');
    const cnpjField = this.querySelector('[data-tax-field="cnpj"]');
    const cpfInput = this.cpfInput;
    const cnpjInput = this.cnpjInput;
    const isCompany = type === 'PJ';

    cpfField?.toggleAttribute('hidden', isCompany);
    cnpjField?.toggleAttribute('hidden', !isCompany);

    if (cpfInput) {
      cpfInput.disabled = isCompany;
      cpfInput.required = !isCompany;
      cpfInput.value = formatCpf(cpfInput.value);
      if (isCompany) {
        cpfInput.value = '';
        cpfInput.removeAttribute('aria-invalid');
      }
    }

    if (cnpjInput) {
      cnpjInput.disabled = !isCompany;
      cnpjInput.required = isCompany;
      cnpjInput.value = formatCnpj(cnpjInput.value);
      if (!isCompany) {
        cnpjInput.value = '';
        cnpjInput.removeAttribute('aria-invalid');
      }
    }
  }

  validate() {
    this.#syncFields();

    const type = this.taxType;
    const input = type === 'PJ' ? this.cnpjInput : this.cpfInput;
    const isValid = type === 'PJ' ? isValidCnpj(input?.value || '') : isValidCpf(input?.value || '');

    if (isValid) {
      this.#clearError();
      input?.removeAttribute('aria-invalid');
      return true;
    }

    const label = type === 'PJ' ? 'CNPJ' : 'CPF';
    this.#showError(`Informe um ${label} v\u00e1lido para continuar.`);
    input?.setAttribute('aria-invalid', 'true');
    input?.focus();

    return false;
  }

  async saveAttributes() {
    const type = this.taxType;
    const cpf = type === 'PF' ? this.cpfInput?.value || '' : '';
    const cnpj = type === 'PJ' ? this.cnpjInput?.value || '' : '';

    await updateCartAttributes({
      tipo_pessoa: type,
      cpf,
      cnpj,
    });
  }

  showSaveError() {
    this.#showError('N\u00e3o foi poss\u00edvel salvar os dados fiscais. Revise os campos e tente novamente.');
  }

  #showError(message) {
    const error = this.querySelector('[data-tax-error]');
    if (!(error instanceof HTMLElement)) return;

    error.textContent = message;
    error.hidden = false;
  }

  #clearError() {
    const error = this.querySelector('[data-tax-error]');
    if (!(error instanceof HTMLElement)) return;

    error.textContent = '';
    error.hidden = true;
  }

  get taxType() {
    const selectedType = this.querySelector('input[name="attributes[tipo_pessoa]"]:checked');

    return selectedType instanceof HTMLInputElement && selectedType.value === 'PJ' ? 'PJ' : 'PF';
  }

  get cpfInput() {
    const input = this.querySelector('[data-tax-input="cpf"]');

    return input instanceof HTMLInputElement ? input : null;
  }

  get cnpjInput() {
    const input = this.querySelector('[data-tax-input="cnpj"]');

    return input instanceof HTMLInputElement ? input : null;
  }
}

if (!customElements.get('macal-cart-tax-fields')) {
  customElements.define('macal-cart-tax-fields', MacalCartTaxFields);
}

let checkoutInProgress = false;

document.addEventListener(
  'click',
  async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const checkoutButton = target.closest('.cart__checkout-button');
    if (!checkoutButton) return;

    await handleCheckoutAttempt(event, findTaxFieldsForElement(checkoutButton));
  },
  true
);

document.addEventListener(
  'submit',
  async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;

    const submitter = event.submitter;
    const isCheckoutSubmit =
      submitter instanceof HTMLElement &&
      submitter.getAttribute('name') === 'checkout';

    if (!isCheckoutSubmit) return;

    await handleCheckoutAttempt(event, findVisibleTaxFields());
  },
  true
);

async function handleCheckoutAttempt(event, taxFields) {
  if (!taxFields) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  if (checkoutInProgress) return;

  if (!taxFields.validate()) return;

  checkoutInProgress = true;

  try {
    await taxFields.saveAttributes();
    window.location.href = getCheckoutUrl();
  } catch (error) {
    console.error(error);
    checkoutInProgress = false;
    taxFields.showSaveError();
  }
}

async function updateCartAttributes(attributes) {
  const body = JSON.stringify({ attributes });

  if (typeof fetch === 'function') {
    const response = await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body,
    });

    if (!response.ok) throw new Error('Could not save cart tax attributes');
    return;
  }

  await updateCartAttributesWithXhr(body);
}

function updateCartAttributesWithXhr(body) {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', Theme.routes.cart_update_url);
    request.setRequestHeader('Content-Type', 'application/json');
    request.setRequestHeader('Accept', 'application/json');
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        resolve();
        return;
      }

      reject(new Error('Could not save cart tax attributes'));
    };
    request.onerror = () => reject(new Error('Could not save cart tax attributes'));
    request.send(body);
  });
}

function getCheckoutUrl() {
  const root = window.Shopify?.routes?.root || '/';
  return `${root.endsWith('/') ? root : `${root}/`}checkout`;
}

function findTaxFieldsForElement(element) {
  const scope = element.closest('.cart-summary__inner, .cart-drawer__summary, .cart-summary');
  const fields = scope?.querySelector('macal-cart-tax-fields');

  return fields instanceof MacalCartTaxFields ? fields : findVisibleTaxFields();
}

function findVisibleTaxFields() {
  const fields = [...document.querySelectorAll('macal-cart-tax-fields')];

  return fields.find((field) => {
    if (!(field instanceof MacalCartTaxFields)) return false;

    const rect = field.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  });
}

function onlyDigits(value) {
  return value.replace(/\D/g, '');
}

function formatCpf(value) {
  const digits = onlyDigits(value).slice(0, CPF_LENGTH);

  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

function formatCnpj(value) {
  const digits = onlyDigits(value).slice(0, CNPJ_LENGTH);

  return digits
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

function isValidCpf(value) {
  const cpf = onlyDigits(value);
  if (cpf.length !== CPF_LENGTH || /^(\d)\1+$/.test(cpf)) return false;

  let sum = 0;
  for (let index = 0; index < 9; index += 1) {
    sum += Number(cpf[index]) * (10 - index);
  }

  let digit = 11 - (sum % 11);
  if (digit >= 10) digit = 0;
  if (digit !== Number(cpf[9])) return false;

  sum = 0;
  for (let index = 0; index < 10; index += 1) {
    sum += Number(cpf[index]) * (11 - index);
  }

  digit = 11 - (sum % 11);
  if (digit >= 10) digit = 0;

  return digit === Number(cpf[10]);
}

function isValidCnpj(value) {
  const cnpj = onlyDigits(value);
  if (cnpj.length !== CNPJ_LENGTH || /^(\d)\1+$/.test(cnpj)) return false;

  const firstWeights = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const secondWeights = [6, ...firstWeights];
  const firstDigit = getCnpjDigit(cnpj.slice(0, 12), firstWeights);
  const secondDigit = getCnpjDigit(`${cnpj.slice(0, 12)}${firstDigit}`, secondWeights);

  return firstDigit === Number(cnpj[12]) && secondDigit === Number(cnpj[13]);
}

function getCnpjDigit(value, weights) {
  const sum = weights.reduce((total, weight, index) => total + Number(value[index]) * weight, 0);
  const remainder = sum % 11;

  return remainder < 2 ? 0 : 11 - remainder;
}
